/**
 * useContentDetails Hook
 *
 * Feature-specific hook for fetching a single user media by ID.
 * Part of clean architecture: Component → Hook → UseCase → Service
 *
 * Pattern:
 * - Encapsulates business logic for single user media fetching
 * - Manages local state for media, loading, error
 * - Does NOT use React Query (component-level state only)
 * - Returns actions and state for the UI
 */

import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import { getUserMediaUseCase } from '@/domain/usecases/userMedia/getUserMediaUseCase';
import { getUserMediaByPathUseCase } from '@/domain/usecases/userMedia/getUserMediaByPathUseCase';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { appLogger } from '@/domain/services/analytics/appLogger';
import type { RootState } from '@/presentation/store';

export interface UseContentDetailsResult {
    media: UserMediaEntity | null;
    isLoading: boolean;
    error: Error | null;
    fetchMedia: () => Promise<void>;
}

/**
 * Hook for fetching a single user media by ID or by relative path.
 *
 * Mirrors Flutter's two-flavor lookup (medias_repository.getMediaByPath /
 * getUserMedia). Pass `{ userMediaId }` for ID lookup, `{ mediaPath }` for path
 * lookup. Path is used by the contentLink chip flow at
 * base_content_widget.dart:283.
 */
export const useContentDetails = (
    params: { userMediaId?: string; mediaPath?: string },
): UseContentDetailsResult => {
    const { userMediaId, mediaPath } = params;
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const [media, setMedia] = useState<UserMediaEntity | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchMedia = useCallback(async () => {
        if (!userMediaId && !mediaPath) {
            setError(new Error('User media ID or path is required'));
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = userMediaId
                ? await getUserMediaUseCase.execute(userMediaId)
                : await getUserMediaByPathUseCase.execute(mediaPath!, currentUser);

            if (!result) {
                setError(new Error('Content not found'));
                setMedia(null);
            } else {
                setMedia(result);
            }
        } catch (err) {
            appLogger.error('Error fetching media', 'useContentDetails.fetchMedia', { context: { userMediaId, mediaPath }, error: err as Error });
            setError(err instanceof Error ? err : new Error('Failed to fetch media'));
            setMedia(null);
        } finally {
            setIsLoading(false);
        }
    }, [userMediaId, mediaPath, currentUser]);

    // Keep the fetched media in sync with global events.
    // These listeners are required so that `fetchedMedia` (returned from this hook) stays
    // up to date — the page mirrors it via a useEffect. Without them, any event fired
    // while navigating away would be lost and the page would show a stale snapshot on return.
    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_LIST_ITEM_REMOVED, ({ mediaId }) => {
            setMedia(prev => prev?.id === mediaId ? null : prev);
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_LIKES_UPDATED, ({ mediaId, likes }) => {
            setMedia(prev => prev?.id === mediaId ? { ...prev, likes } : prev);
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_COMMENTS_UPDATED, ({ mediaId, comments }) => {
            setMedia(prev => prev?.id === mediaId ? { ...prev, comments } : prev);
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, ({ mediaId, patch }) => {
            setMedia(prev => prev?.id === mediaId ? { ...prev, ...patch } : prev);
        });
    }, []);

    return {
        media,
        isLoading,
        error,
        fetchMedia,
    };
};
